import classNames from 'classnames';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { DemoPill, StateMessage } from '@/components/ui/controls';
import { Card, PageHeader } from '@/components/ui/page';
import { btn, field } from '@/components/ui/styles';
import { STAGES } from '@/utils/labels';
import http from '@/utils/http';
import { useApi } from '@/utils/use-api';
import { useSegments } from '@/utils/use-segments';

import type { Template, Trigger, TriggerEvent, TriggerInput } from './api-types';
import { TRIGGER_EVENTS, channelPhrase, describeDelay, describeEvent } from './channels';
import MessagingNav from './messaging-nav';
import { ActionBar, ChannelPicker, NotFound, PreviewPanel, SegmentPicker, SkippedWarnings, Toggle, errorStatus, useAudience } from './parts';

type Unit = 'hours' | 'days';

/** "A casino goes live: after 2 hours, send “Welcome…” by email and Telegram, to everyone it happens to." */
const summary = (t: TriggerInput, template: Template | undefined, segmentName: (id: string) => string) => {
  const who = t.segment_ids.length ? `to ${t.segment_ids.map(segmentName).join(', ')}` : 'to everyone it happens to';
  const how = t.channels.length ? `by ${channelPhrase(t.channels)}` : 'on no channel yet';
  return `${describeEvent(t)}: ${describeDelay(t.delay_hours).toLowerCase()}, send “${template?.name ?? 'no template'}” ${how}, ${who}.`;
};

/** The editor for one trigger; keyed by id so switching triggers starts fresh. */
const Editor = ({ stored, templates }: { stored?: Trigger; templates: Template[] }) => {
  const navigate = useNavigate();
  const { byId } = useSegments();
  const [t, setT] = useState<TriggerInput>(() =>
    stored
      ? { name: stored.name, event: stored.event, params: stored.params, segment_ids: stored.segment_ids, delay_hours: stored.delay_hours, template_id: stored.template_id, channels: stored.channels, active: stored.active }
      : { name: '', event: 'casino_launched', params: {}, segment_ids: [], delay_hours: 0, template_id: templates[0]?.id ?? '', channels: ['email'], active: true },
  );
  const [unit, setUnit] = useState<Unit>(() => (t.delay_hours && t.delay_hours % 24 === 0 ? 'days' : 'hours'));
  const [notice, setNotice] = useState<'saved' | 'failed' | 'invalid' | null>(null);
  const [busy, setBusy] = useState(false);
  const audience = useAudience(t.segment_ids, t.channels);
  const set = (patch: Partial<TriggerInput>) => {
    setT((x) => ({ ...x, ...patch }));
    setNotice(null);
  };

  const delayValue = unit === 'days' ? t.delay_hours / 24 : t.delay_hours;
  const template = templates.find((x) => x.id === t.template_id);
  const event = TRIGGER_EVENTS.find((e) => e.id === t.event);

  const save = async () => {
    setBusy(true);
    setNotice(null);
    try {
      const body = { ...t, name: t.name.trim() };
      const { data } = stored ? await http.put<Trigger>(`/triggers/${stored.id}`, body) : await http.post<Trigger>('/triggers', body);
      if (stored) setNotice('saved');
      else navigate(`/messaging/triggers/${data.id}`, { replace: true });
    } catch (err) {
      setNotice(errorStatus(err) === 422 ? 'invalid' : 'failed');
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!stored) return;
    setBusy(true);
    try {
      await http.delete(`/triggers/${stored.id}`);
      navigate('/messaging/triggers');
    } catch {
      setNotice('failed');
      setBusy(false);
    }
  };

  return (
    <div className="grid grid-cols-1 gap-4 md:gap-6 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
      <Card aside={stored?.demo && <DemoPill title="From the local demo seed">Demo</DemoPill>}>
        <label className={field.label} htmlFor="trigger-name">
          Name
        </label>
        <input id="trigger-name" className={field.input} placeholder="Welcome a new casino" value={t.name} onChange={(e) => set({ name: e.target.value })} />

        <h2 className="mt-6 text-sm text-fg-strong">When</h2>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <select
            aria-label="Event"
            className={classNames(field.select, 'w-full sm:w-auto')}
            value={t.event}
            onChange={(e) => {
              const next = e.target.value as TriggerEvent;
              set({ event: next, params: next === 'no_players' ? { days: 30 } : next === 'stage_changed' ? { stage: 'live' } : {} });
            }}
          >
            {TRIGGER_EVENTS.map((e) => (
              <option key={e.id} value={e.id}>
                {e.off ? `${e.label} (not in this version)` : e.label}
              </option>
            ))}
          </select>
          {t.event === 'no_players' && (
            <label className="inline-flex items-center gap-2 text-sm text-fg">
              for
              <input
                type="number"
                min={1}
                max={365}
                aria-label="Days without players"
                className={classNames(field.input, 'w-20')}
                value={t.params.days ?? 30}
                onChange={(e) => set({ params: { days: Math.max(1, Number(e.target.value) || 1) } })}
              />
              days
            </label>
          )}
          {t.event === 'stage_changed' && (
            <select aria-label="Stage" className={field.select} value={t.params.stage ?? 'live'} onChange={(e) => set({ params: { stage: e.target.value } })}>
              {STAGES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          )}
        </div>
        {event && <p className="mt-1 text-xs text-fg-muted">{event.hint}</p>}

        <h2 className="mt-6 text-sm text-fg-strong">Who</h2>
        <div className="mt-2">
          <SegmentPicker value={t.segment_ids} onChange={(segment_ids) => set({ segment_ids })} />
          <p className="mt-1 text-xs text-fg-muted">No segment and no country: everyone the event happens to.</p>
          {/* A segment that would reach everyone makes the trigger skip what happens, until it can narrow. */}
          {audience.data && <SkippedWarnings audience={audience.data} />}
        </div>

        <h2 className="mt-6 text-sm text-fg-strong">Wait</h2>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <input
            type="number"
            min={0}
            aria-label="Wait"
            className={classNames(field.input, 'w-24')}
            value={delayValue}
            onChange={(e) => set({ delay_hours: Math.max(0, Number(e.target.value) || 0) * (unit === 'days' ? 24 : 1) })}
          />
          <select
            aria-label="Unit"
            className={field.select}
            value={unit}
            onChange={(e) => {
              const next = e.target.value as Unit;
              setUnit(next);
              set({ delay_hours: delayValue * (next === 'days' ? 24 : 1) });
            }}
          >
            <option value="hours">hours</option>
            <option value="days">days</option>
          </select>
          <span className="text-xs text-fg-muted">0 sends right away.</span>
        </div>

        <h2 className="mt-6 text-sm text-fg-strong">Send</h2>
        {templates.length ? (
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <select
              aria-label="Template"
              className={classNames(field.select, 'w-full sm:w-auto')}
              value={t.template_id}
              onChange={(e) => set({ template_id: e.target.value })}
            >
              {templates.map((tpl) => (
                <option key={tpl.id} value={tpl.id}>
                  {tpl.name}
                </option>
              ))}
            </select>
            {t.template_id && (
              <Link to={`/messaging/templates/${t.template_id}`} className={btn.link}>
                Edit template
              </Link>
            )}
          </div>
        ) : (
          <p className="mt-2 text-sm text-fg-muted">
            A trigger sends a template.{' '}
            <Link to="/messaging/templates/new" className={btn.link}>
              Make a template first
            </Link>
          </p>
        )}
        <div className="mt-3">
          <ChannelPicker value={t.channels} onChange={(channels) => set({ channels })} />
        </div>

        <div className="mt-6 flex items-center gap-3 text-sm text-fg">
          <Toggle label="Active" checked={t.active} onChange={(active) => set({ active })} />
          {t.active ? 'Active: sends whenever the event happens from now on' : 'Paused: sends nothing'}
        </div>

        <ActionBar className="flex flex-wrap items-center gap-2 md:justify-end md:gap-3">
          {notice === 'saved' && (
            <span role="status" className="basis-full text-sm text-fg-muted md:basis-auto">
              Saved
            </span>
          )}
          {notice === 'invalid' && (
            <span className="basis-full text-sm text-danger md:basis-auto">Check the name, the template, the channels and the event’s settings.</span>
          )}
          {notice === 'failed' && <span className="basis-full text-sm text-danger md:basis-auto">That did not work. Try again.</span>}
          {stored && (
            <button type="button" className={classNames(btn.danger, 'flex-1 md:flex-none')} disabled={busy} onClick={remove}>
              Delete
            </button>
          )}
          <button
            type="button"
            className={classNames(btn.primary, 'flex-1 md:flex-none')}
            disabled={!t.name.trim() || !t.channels.length || !t.template_id || busy}
            onClick={save}
          >
            Save trigger
          </button>
        </ActionBar>
      </Card>

      <div className="space-y-4 md:space-y-6">
        <Card title="In short">
          <p className="text-sm text-fg">{summary(t, template, (id) => byId(id)?.name ?? 'a deleted segment')}</p>
          {stored && <p className="mt-2 text-xs text-fg-muted">Sent {stored.sent_30d} times in the last 30 days.</p>}
          {!['casino_launched', 'lead_added'].includes(t.event) && (
            <p className="mt-2 text-xs text-fg-muted">Not evaluated yet: this event is stored, but nothing sends until the trigger list is settled (Q14).</p>
          )}
        </Card>
        {template && (
          <Card title="What it sends" aside={<span className="text-xs text-fg-faint">With sample values</span>}>
            <PreviewPanel channels={t.channels} title={template.subject || template.name} body={template.body} />
          </Card>
        )}
      </div>
    </div>
  );
};

/** One trigger (OUT-3): when it fires, for whom, after how long, and what it sends. */
const TriggerEditorPage = () => {
  const { id } = useParams();
  const trigger = useApi<Trigger>(id ? `/triggers/${encodeURIComponent(id)}` : null);
  const templates = useApi<Template[]>('/templates');

  let body;
  if (trigger.error === 'Not found.') body = <NotFound what="trigger" back="/messaging/triggers" label="Back to triggers" />;
  else if ((trigger.error && !trigger.data) || (templates.error && !templates.data)) {
    body = <StateMessage title="The trigger could not be loaded" tone="error">{trigger.error ?? templates.error}</StateMessage>;
  } else if ((id && !trigger.data) || !templates.data) body = <div className="h-64 animate-pulse rounded-card bg-surface shadow-e1" />;
  else body = <Editor key={id ?? 'new'} stored={trigger.data ?? undefined} templates={templates.data} />;

  return (
    <div>
      <PageHeader
        title={id ? (trigger.data?.name ?? 'Trigger') : 'New trigger'}
        description="When something happens, wait, then send a template on the channels you pick."
        back={{ to: '/messaging/triggers', label: 'Triggers' }}
      >
        <MessagingNav />
      </PageHeader>
      {body}
    </div>
  );
};

export default TriggerEditorPage;
